import { useEffect, useState } from "react";
import type { MessageResponseAggregation, MessageDeliveryStatus, ResponseOption } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { X, BarChart3, Users, Send, Eye, MessageSquare, CheckCircle2 } from "lucide-react";

interface MessageResponseDashboardProps {
  messageId: string;
  responseOptions: ResponseOption[];
  onClose: () => void;
}

const COLOR_CLASSES: Record<string, string> = {
  green: "bg-green-500",
  red: "bg-red-500",
  yellow: "bg-yellow-500",
};

export function MessageResponseDashboard({
  messageId,
  responseOptions,
  onClose,
}: MessageResponseDashboardProps) {
  const [aggregation, setAggregation] = useState<MessageResponseAggregation | null>(null);
  const [delivery, setDelivery] = useState<MessageDeliveryStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [respRes, recipRes] = await Promise.all([
          api.getMessageResponses(messageId),
          api.getMessageRecipients(messageId),
        ]);
        setAggregation(respRes.aggregation);
        setDelivery(recipRes.delivery);
      } catch (e) {
        setError(e instanceof api.ApiError ? e.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [messageId]);

  if (loading) {
    return (
      <Card className="w-full max-w-lg" data-component="response-dashboard" data-state="loading">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-lg" data-component="response-dashboard" data-state="error">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive" data-error>{error}</p>
        </CardContent>
      </Card>
    );
  }

  const totalResponded = aggregation?.responded_count ?? 0;
  const totalRecipients = aggregation?.total_recipients ?? 0;

  return (
    <Card className="w-full max-w-lg" data-component="response-dashboard" data-state="ready">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Response Dashboard
          </CardTitle>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Delivery lifecycle bar */}
        {delivery && (
          <div>
            <p className="text-sm font-medium mb-2">Delivery Status</p>
            <div className="flex gap-4 text-center">
              <DeliveryStep icon={<Send className="h-4 w-4" />} label="Sent" count={delivery.total} />
              <DeliveryStep icon={<CheckCircle2 className="h-4 w-4" />} label="Delivered" count={delivery.delivered} />
              <DeliveryStep icon={<Eye className="h-4 w-4" />} label="Read" count={delivery.read} />
              <DeliveryStep icon={<MessageSquare className="h-4 w-4" />} label="Responded" count={delivery.responded} />
            </div>
            {/* Progress bar */}
            <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden flex">
              {delivery.total > 0 && (
                <>
                  <div
                    className="h-full bg-blue-400 transition-all"
                    style={{ width: `${(delivery.delivered / delivery.total) * 100}%` }}
                  />
                  <div
                    className="h-full bg-indigo-500 transition-all"
                    style={{ width: `${((delivery.read - delivery.delivered) / delivery.total) * 100}%` }}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Vote breakdown */}
        <div>
          <p className="text-sm font-medium mb-2">
            Responses ({totalResponded} / {totalRecipients})
          </p>
          <div className="flex flex-col gap-1.5">
            {responseOptions.map((opt) => {
              const count = aggregation?.counts[opt.value] ?? 0;
              const pct = totalRecipients > 0 ? (count / totalRecipients) * 100 : 0;
              return (
                <div key={opt.value} className="flex items-center gap-2" data-item="response-option" data-item-id={opt.value} data-response-count={count}>
                  <span className="text-sm w-28 truncate">{opt.label}</span>
                  <div className="flex-1 h-5 rounded bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded transition-all ${COLOR_CLASSES[opt.color ?? ""] ?? "bg-primary"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-12 text-right">{count}</span>
                </div>
              );
            })}
            {/* No response count */}
            {totalRecipients - totalResponded > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm w-28 truncate text-muted-foreground">No response</span>
                <div className="flex-1 h-5 rounded bg-muted overflow-hidden">
                  <div
                    className="h-full rounded bg-gray-300 transition-all"
                    style={{ width: `${((totalRecipients - totalResponded) / totalRecipients) * 100}%` }}
                  />
                </div>
                <span className="text-sm text-muted-foreground w-12 text-right">{totalRecipients - totalResponded}</span>
              </div>
            )}
          </div>
        </div>

        {/* Recipient drill-down */}
        {aggregation && aggregation.recipients.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2 flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              Recipients
            </p>
            <div className="max-h-48 overflow-y-auto rounded border divide-y text-sm">
              {aggregation.recipients.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between px-3 py-2">
                  <div>
                    <p className="font-medium">{r.user_name}</p>
                    <p className="text-xs text-muted-foreground">{r.group}</p>
                  </div>
                  <div className="text-right">
                    {r.latest_response ? (
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        getResponseColor(r.latest_response, responseOptions)
                      }`}>
                        {getResponseLabel(r.latest_response, responseOptions)}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No response</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DeliveryStep({ icon, label, count }: { icon: React.ReactNode; label: string; count: number }) {
  return (
    <div className="flex flex-col items-center gap-1 flex-1">
      <div className="text-muted-foreground">{icon}</div>
      <span className="text-lg font-semibold">{count}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function getResponseLabel(value: string, options: ResponseOption[]): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

function getResponseColor(value: string, options: ResponseOption[]): string {
  const color = options.find((o) => o.value === value)?.color;
  const map: Record<string, string> = {
    green: "bg-green-100 text-green-800",
    red: "bg-red-100 text-red-800",
    yellow: "bg-yellow-100 text-yellow-800",
  };
  return color ? map[color] ?? "bg-gray-100 text-gray-800" : "bg-gray-100 text-gray-800";
}
